import { Component, EventEmitter, Input, NgZone, OnInit, Output, ViewChild, ViewChildren } from '@angular/core';
import { EntityService } from '../../../entities/services/entity.service';
import { PopoverDirective } from 'ngx-bootstrap';
// import { CoreModule } from '../../../core.module';
// import { FieldFormComponent } from '../../../entities/components/field-form/field-form.component';
import { Subject } from 'rxjs/Subject';
import { ToolbarService } from '../../../layouts/toolbar/services/toolbar.service';
import { pluckPath } from '../../../support/helpers/pluck-path.function';
import { ActivatedRoute } from '@angular/router';
import { EntityModel } from '../../models/entity-model';
import { AlertService } from '../../../alerts/services/alert.service';
import { EntityFormService } from '../../../forms/form/services/entity-form.service';
import { FormModel } from '../../../forms/form/models/form.model';
import { ModalService } from '../../../modals/services/modal.service';
import { RouteService } from '../../../routing/services/route.service';
import { ToastService } from '../../../toast/services/toast.service';
import { DependencyInjector } from '../../../support/injector';
import { trans } from '../../../localization/helpers';


@Component({
    selector: 'chassis-form-builder',
    templateUrl: './form-builder.component.html',
    styleUrls: ['./form-builder.component.scss'],
    providers: [EntityFormService, AlertService]
})
export class FormBuilderComponent implements OnInit {

    @ViewChildren(PopoverDirective) popoverElements;
    @ViewChildren('newRowDroppable') newRowDroppables;
    @ViewChild('groupNameInput') groupNameInput;
    @ViewChild('sectionNameInput') sectionNameInput;

    @Input() public entityId    = null;
    @Input() public formId      = null;
    @Input() public routePrefix = '';

    @Output() public saved: EventEmitter<any> = new EventEmitter();

    public fields: Array<any>;
    public fieldsInForm: Array<any>          = [];
    public draggingNewElement                = false;
    public draggingStopped: Subject<boolean> = new Subject();

    public groupName      = '';
    public sectionName    = '';
    public fieldEditMode  = '';
    public editID         = '';
    public groupNameEdit;
    public fieldEdit: any = {};
    public entity: EntityModel;
    public form: FormModel;
    public formLoading    = false;
    public errors         = {};

    constructor(protected formService: EntityFormService,
                protected modalService: ModalService,
                protected toolbarService: ToolbarService,
                protected alertService: AlertService,
                protected entityService: EntityService,
                protected toastService: ToastService,
                protected zone: NgZone,
                protected routeService: RouteService,
                protected route: ActivatedRoute) {

    }

    ngAfterViewChecked() {
        console.log('view checked');
    }

    public ngOnInit(): void {

        /**
         * Get form from entity service
         */
        this.entityService
            .one(parseInt(this.entityId, 10))
            .subscribe(entity => {

                this.entity = entity;

                if (this.formId !== null) {
                    console.warn('resolve form', this.formId);
                    this.form                        = entity.forms().find('id', parseInt(this.formId));
                    this.form._service               = new EntityFormService(DependencyInjector.injector);
                    this.form._service.defaultParams = {entityId: entity.id};

                    this.fieldsInForm = pluckPath(this.form.groups, 'sections.rows.elements.name', true);
                } else {
                    this.form                        = entity.forms().makeModel();
                    this.form._service               = new EntityFormService(DependencyInjector.injector);
                    this.form._service.defaultParams = {entityId: entity.id};

                    this.groupName = 'General';
                    const group    = this.addGroup();

                    this.addSection(group.id);
                }

            });

        /**
         * Get the fields from entity service
         */
        this.entityService
            .one(parseInt(this.entityId)).subscribe(entity => {

            this.fields = entity.fieldsMeta().all().map(field => {
                field.field_meta_id = field.id;
                delete field.id;

                return field;
            });

        });

        this.createToolbar();
    }

    /**
     * Set the toolbar
     *
     */
    public createToolbar() {
        this.toolbarService.add({
            name: 'cancel',
            text: 'Cancel',
            callback: () => {
                this.routeService.navigate('entities.edit', [this.route.snapshot.params['entity']]);
            }
        });

        this.toolbarService.add({
            name: 'submit',
            text: trans('Submit'),
            callback: () => {
                this.formLoading = true;
                const formId     = this.form.id;

                this.form.save().subscribe((result: FormModel) => {
                    this.formLoading = false;
                    this.errors      = {};
                    this.toastService.successSaved();

                    // check if the form is saved for the first time
                    // -> if so, add to the forms collection
                    if (result.id !== formId) {
                        this.entity.forms().push(result);
                    }

                    this.saved.emit(result);
                }, error => {
                    // this.errors      = error._body.errors;
                    this.formLoading = false;
                    this.toastService.error('api-error');
                });
            },
            loader: () => {
                return this.formLoading;
            }
        });
    }

    /**
     * Focus on group popover input
     */
    public focusOnInput(group = null) {
        this.groupNameEdit = group || '';

        setTimeout(() => {
            if (this.groupNameInput) {
                this.groupName = group ? group.name : '';
                this.groupNameInput.nativeElement.focus();
            }
        }, 50);
    }

    /**
     * Focus on section popover input
     *
     * @param section
     */
    public focusOnSectionInput(section) {
        this.sectionName = section.name;
        this.editID      = section.id;

        setTimeout(() => {
            if (this.sectionNameInput) {
                this.sectionNameInput.nativeElement.focus();
            }
        }, 50);
    }

    public focusOnFieldSizePopover(field) {
        this.fieldEdit = field;
    }

    public changeFieldSize(size) {
        this.fieldEdit.space = size;
        this.fieldEdit       = {};
        this.hidePopovers();
    }

    /**
     * Hide all open popovers
     */
    public hidePopovers() {
        if (this.popoverElements) {
            this.popoverElements.forEach(element => {
                element.hide();
            });
        }
    }

    /**
     * Add a new group
     *
     * @returns {boolean}
     */
    public addGroup() {
        let group;

        // when empty return false
        if (this.groupName === '') {
            return false;
        }

        if (this.groupNameEdit) {
            this.groupNameEdit.name = this.groupName;
            group                   = this.form.updateGroup(this.groupNameEdit);
        } else {
            group = this.form.addGroup(this.groupName);
        }

        this.groupName = '';
        this.hidePopovers();

        return group;
    }

    /**
     * Add a new section
     *
     * @param groupID
     */
    public addSection(groupID: number) {
        this.form.addSection(groupID, '');
    }

    /**
     * Edit section
     *
     * @param sectionID
     * @returns {boolean}
     */
    public editSection() {
        // when empty return false
        if (this.sectionName === '') {
            return false;
        }

        this.form.updateSection({
            id: parseInt(this.editID, 10),
            name: this.sectionName
        });

        this.sectionName = '';
        this.editID      = '';
        this.hidePopovers();
    }

    /**
     * Delete a complete group
     *
     * @param group
     */
    public deleteGroup(group) {
        // this.alertService.create('Are you sure you want to delete this group?', 'Note that all child elements will be deleted as well.', () => {
        //     const deletedElements = this.form.deleteGroup(group.id);
        //     deletedElements.forEach(element => {
        //         this.deleteFromFieldsInForm(element);
        //     });
        // });
    }

    /**
     * Delete given section
     *
     * @param section
     */
    public deleteSection(section) {
        // this.alertService.create('Are you sure you want to delete this section?',
        // 'Note that all child elements will be deleted as well.', () => {
        //     const deletedElements = this.form.deleteSection(section.id);
        //     deletedElements.forEach(element => {
        //         this.deleteFromFieldsInForm(element);
        //     });
        // });
    }

    /**
     * Add a new row to the given section
     *
     * @param sectionID
     */
    public addRow(sectionID) {
        return this.form.addRow(parseInt(sectionID, 10));
    }

    /**
     * Delete given row
     *
     * @param row
     */
    public deleteRow(row) {
        const alert = this.alertService.open({
            message: 'Are you sure you want to delete this row? ' +
                'Note that all child elements will be deleted as well.'
        });

        alert.closed(() => {
            const deletedElements = this.form.deleteRow(row.id);
            deletedElements.forEach(element => {
                this.deleteFromFieldsInForm(element);
            });
        })
    }

    /**
     * Edit field
     *
     * @param field
     */
    public editField(field, onSubmit: Function, onChange: Function, onClose: Function) {
        this.fieldEditMode = field.id;
        // create modal
        // this.modalService.create(CoreModule, FieldFormComponent, {type: 'slide-right', size: 'sm'}, {
        //     field: field,
        //     onSubmit: () => {
        //         this.fieldEditMode = '';
        //
        //         // call the callback if set
        //         if (onSubmit) {
        //             onSubmit();
        //         }
        //     },
        //     onChange: (updatedField) => {
        //         this.form.updateField(updatedField);
        //
        //         // call the callback if set
        //         if (onChange) {
        //             onChange(updatedField);
        //         }
        //     },
        //     onClose: () => {
        //         this.fieldEditMode = '';
        //
        //         // call the callback if set
        //         if (onClose) {
        //             onClose();
        //         }
        //     }
        // });
    }

    public addFieldToNewRow(sectionID, field) {
        const row = this.addRow(sectionID);
        this.form.addField(row.id, field);
        this.fieldsInForm.push(field.name);

        // remove item from DOM
        // event.ui.draggable.remove();
    }

    public droppedNewRow(event) {
        this.zone.run(() => {
            this.addFieldToNewRow(event.droppable.id, event.draggable);
        });
    }

    public droppedExistingRow(event, rowID) {
        this.zone.run(() => {
            event.ui.draggable.attr('style', '');
            this.form.addField(rowID, event.draggable);
            this.fieldsInForm.push(event.draggable.name);
        });
    }

    /**
     * Get element by type from entity service
     *
     * @param type
     * @returns {{name: string, icon: string, type: string}}
     */
    public getElementByType(type) {
        return this.entityService.getElementByType(type);
    }

    public orderUpdated() {

    }

    public onDropField(event, row) {
        this.form.addField(row.id, event.data, event.index);
        this.fieldsInForm.push(event.data.name);
    }

    public onRemoveField(event, row) {
        this.form.deleteField(event.data.uuid);
    }

    public draggableOptions() {
        return {
            revert: 'invalid',
            stop: () => {
                this.draggingStopped.next(true);
            }
        };
    }

    public fieldSortableOptions(rowID) {
        return {
            revert: true,
            handle: '.inner > a.handle',
            connectWith: '.field-sortable',
            sort: (event, ui) => {
                this.adaptPlaceholderWidth(event, ui, rowID);
            },
            over: (event, ui) => {
                this.adaptPlaceholderWidth(event, ui, rowID);
            },
            activate: (event, ui) => {
                console.warn('activate field sortable');

                const rowSpace      = this.form.getRowSpace(rowID);
                const draggingField = ui.item.data('drag-drop-item');
                const senderRow     = this.form.getFieldRow(draggingField.uuid);

                if (rowSpace === 0 && rowID !== senderRow.id) {
                    // disable the sortable
                    (<any>jQuery(event.target)).sortable('disable');
                    (<any>jQuery(ui.sender)).sortable('refresh');

                    // subscribe to dragging observable and watch for a stop in order to re-enable the sortable again
                    const subscription = this.draggingStopped.subscribe(() => {
                        (<any>jQuery(event.target)).sortable('enable');
                        subscription.unsubscribe();
                    });
                }
            },
            beforeStop: (event) => {
                // trigger
                this.draggingStopped.next(true);
            }
        };
    }

    public droppableRowOptions(rowID) {
        return {
            accept: '.draggable-element',
            hoverClass: 'highlight-hover',
            activeClass: 'highlight',
            activate: (event, ui) => {

                console.warn('activate droppable row');

                if (this.form.getRowSpace(rowID) === 0) {
                    (<any>jQuery(event.target)).droppable('disable');
                }

                const subscription = this.draggingStopped.subscribe(() => {
                    (<any>jQuery(event.target)).droppable('enable').removeClass('highlight');
                    subscription.unsubscribe();
                });
            }
        };
    }

    protected adaptPlaceholderWidth(event, ui, rowID) {
        console.warn('adapt placeholder width', ui);
        const draggingField = ui.item.data('drag-drop-item');
        const senderRow     = this.form.getFieldRow(draggingField.uuid);

        const rowSpace = senderRow.id === rowID ? (this.form.getRowSpace(rowID) + draggingField.space) : this.form.getRowSpace(rowID);

        jQuery(event.target).find('.chassis-drag-drop-placeholder').addClass('col-xs-' + rowSpace);
    }

    public startDraggingElements(event) {

    }

    public deleteField(field) {
        // this.alertService.create('Are you sure you want to delete this field?', '', () => {
        //     this.deleteFromFieldsInForm(field);
        //     this.form.deleteField(field.uuid);
        // });
    }

    protected deleteFromFieldsInForm(field) {
        console.warn('fields in form', this.fieldsInForm);
        this.fieldsInForm.splice(this.fieldsInForm.indexOf(field.name), 1);
        console.warn('delete field from fields in form', field, this.fieldsInForm, this.fieldsInForm.indexOf(field.name));
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/entities/components/form-builder/form-builder.component.ts